// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Alert, Anchor, Button, Container, Stack, Text, TextInput, Title } from '@mantine/core';
import { MedplumClient } from '@medplum/core';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useState } from 'react';
import { getConfig } from '../config';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const RECAPTCHA_SCRIPT_ID = 'patient-app-recaptcha-script';

let recaptchaReadyPromise: Promise<void> | undefined;

function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (window.grecaptcha) {
    return Promise.resolve();
  }

  if (recaptchaReadyPromise) {
    return recaptchaReadyPromise;
  }

  recaptchaReadyPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA script')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
    document.head.appendChild(script);
  });

  return recaptchaReadyPromise;
}

async function getRecaptchaToken(siteKey: string): Promise<string> {
  await loadRecaptchaScript(siteKey);

  if (!window.grecaptcha) {
    throw new Error('reCAPTCHA is unavailable');
  }

  return new Promise((resolve, reject) => {
    window.grecaptcha?.ready(() => {
      window.grecaptcha?.execute(siteKey, { action: 'forgot_password' }).then(resolve).catch(reject);
    });
  });
}

export function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setLoading(true);
    try {
      const { baseUrl, projectId, recaptchaSiteKey } = getConfig();
      if (!baseUrl || !projectId || !recaptchaSiteKey) {
        throw new Error('Missing Medplum reset-password configuration');
      }

      const recaptchaToken = await getRecaptchaToken(recaptchaSiteKey);
      const medplum = new MedplumClient({ baseUrl });
      await medplum.post('auth/resetpassword', {
        email,
        projectId,
        sendEmail: false,
        recaptchaSiteKey,
        recaptchaToken,
      });
    } catch (err) {
      // Swallow errors: the confirmation must look identical whether or not the
      // email matches an account (no user enumeration). Report to Sentry only.
      Sentry.captureException(err);
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  }

  if (submitted) {
    return (
      <Container size="xs" mt="xl">
        <Stack gap="md">
          <Title order={2}>Check your email</Title>
          <Alert color="blue">
            If an account exists for that email address, we&apos;ve sent a link to reset your password.
          </Alert>
          <Anchor href="/signin">Back to sign in</Anchor>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="xs" mt="xl">
      <Stack gap="md">
        <Title order={2}>Reset your password</Title>
        <Text c="dimmed" size="sm">
          Enter your email address and we&apos;ll send you a link to reset your password.
        </Text>
        <form onSubmit={handleSubmit}>
          <Stack gap="sm">
            <TextInput
              label="Email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              required
            />
            <Button type="submit" loading={loading} fullWidth>
              Send reset link
            </Button>
            <Anchor href="/signin" size="sm">
              Back to sign in
            </Anchor>
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}
